import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import api from '@/services/api';

/**
 * Custom-vs-Fixed pricing collision detector.
 *
 * For every ACTIVE fixed plan (Starter/Pro/etc.), we compute what the custom
 * plan builder would charge a user for the SAME limits using the current
 * pricing_config (base costs × profit margin). If the fixed price ≥ custom
 * price, a rational user would just pick the custom slider and get equal or
 * more value → the fixed plan cannibalises itself. Admin needs to see this.
 *
 * The card shows three signal states per plan:
 *   • overpriced  → fixed > custom by ≥5%  (admin should lower or add value)
 *   • equal        → within ±5%             (admin should differentiate or drop)
 *   • good         → fixed < custom          (fixed offers a discount, healthy)
 */
export const PricingCollisionCard = ({ plans }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get('/pricing-config/admin');
      setConfig(r.data);
    } catch (e) {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const analysis = useMemo(() => {
    if (!config || !plans?.length) return [];
    const bc = config.base_costs || {};
    // Profit-margin semantics (matches backend iter126): price = cost / (1 - m/100).
    // Capped at 95% for safety (mirrors backend MAX_PROFIT_MARGIN_PCT).
    const marginPct = Math.min(Math.max(config.profit_margin ?? 30, 0), 95);
    const mult = 1 / (1 - marginPct / 100);

    return plans
      .filter((p) => p.is_active && p.billing_type !== 'one_time' && (p.monthly_price || 0) > 0)
      .map((p) => {
        const docs = p.limits?.documents ?? p.document_limit ?? 10;
        const bots = p.limits?.chatbots ?? p.chatbot_limit ?? 1;
        const msgs = p.limits?.messages ?? p.message_limit ?? 1000;
        const storage = p.limits?.storage_mb ?? p.storage_mb_limit ?? 100;
        const customBase =
          docs * (bc.per_document || 0) +
          bots * (bc.per_chatbot || 0) +
          (msgs / 1000) * (bc.per_1000_messages || 0) +
          (storage / 100) * (bc.per_100mb_storage || 0);
        const customPrice = Math.round(customBase * mult * 100) / 100;
        const fixedPrice = p.monthly_price || 0;
        const delta = fixedPrice - customPrice;
        const deltaPct = customPrice > 0 ? (delta / customPrice) * 100 : 0;
        let status = 'good';
        if (Math.abs(deltaPct) < 5) status = 'equal';
        else if (deltaPct > 5) status = 'overpriced';
        return {
          plan_id: p.plan_id,
          name: p.name,
          fixed_price: fixedPrice,
          custom_price: customPrice,
          delta,
          deltaPct,
          status,
        };
      });
  }, [config, plans]);

  const collisions = analysis.filter((a) => a.status !== 'good');

  // Plan Recommendation Tag (iter126): expose the healthiest-margin
  // "good" plan via a custom event so AdminPlansTab can badge it as
  // "Most popular" without prop-drilling. Only recommends plans where
  // fixed_price < custom_price (i.e. good/discount status) AND that have
  // the smallest deltaPct (closest to break-even, still profitable).
  React.useEffect(() => {
    if (!analysis.length) return;
    const eligible = analysis.filter((a) => a.status === 'good' && a.delta < 0);
    // Smallest |deltaPct| among "good" = margin closest to zero = admin
    // squeezing the most price from the market before losing to custom.
    const winner = eligible.length
      ? eligible.reduce((best, cur) => (Math.abs(cur.deltaPct) < Math.abs(best.deltaPct) ? cur : best))
      : null;
    try {
      window.dispatchEvent(new CustomEvent('harmonix:recommended-plan', {
        detail: { plan_id: winner?.plan_id || null },
      }));
    } catch { /* SSR safe */ }
  }, [analysis]);

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm mt-6">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!config || analysis.length === 0) return null;

  return (
    <Card className="bg-white border-slate-200 shadow-sm mt-6" data-testid="pricing-collision-card">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          {collisions.length > 0 ? (
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          )}
          Custom vs Fixed Plan Analysis
        </CardTitle>
        <CardDescription className="text-slate-600">
          For each active fixed plan, we compute what the custom plan builder would charge for the same limits using your current base costs × {config.profit_margin}% profit margin. Watch for cannibalisation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {collisions.length > 0 && (
          <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
            <strong>{collisions.length} plan(s) at risk.</strong> A user shopping on the Pricing page can build the same tier via the custom slider for a similar or lower price — so the fixed plan doesn&apos;t sell. Either raise the custom margin, add exclusive features to the fixed plan, or lower the fixed price.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Plan</th>
                <th className="text-right px-3 py-2 font-medium">Fixed Price</th>
                <th className="text-right px-3 py-2 font-medium">Custom for Same Limits</th>
                <th className="text-right px-3 py-2 font-medium">Δ</th>
                <th className="text-center px-3 py-2 font-medium">Signal</th>
              </tr>
            </thead>
            <tbody>
              {analysis.map((row) => (
                <tr
                  key={row.plan_id}
                  className={`border-b last:border-b-0 ${
                    row.status === 'overpriced' ? 'bg-red-50/40' :
                    row.status === 'equal' ? 'bg-amber-50/40' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`collision-row-${row.plan_id}`}
                >
                  <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                  <td className="px-3 py-2 text-right font-mono">${row.fixed_price.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-600">${row.custom_price.toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${
                    row.delta > 0 ? 'text-red-700' : row.delta < 0 ? 'text-emerald-700' : 'text-slate-600'
                  }`}>
                    {row.delta > 0 ? '+' : ''}${row.delta.toFixed(2)} ({row.deltaPct > 0 ? '+' : ''}{row.deltaPct.toFixed(0)}%)
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.status === 'overpriced' && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
                        <AlertTriangle className="w-3 h-3" /> Overpriced
                      </Badge>
                    )}
                    {row.status === 'equal' && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                        <ArrowRight className="w-3 h-3" /> Same as custom
                      </Badge>
                    )}
                    {row.status === 'good' && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Discounted vs custom
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingCollisionCard;
